// Small shared pieces for the Provider Directory screens: modal shell, the
// F001 / D001 reference chip, status pills, and the form styles used by the
// facility and physician editors. Everything reads the app's CSS tokens.
import { fmtPhone } from '../../lib/directory';

export const label = { fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
export const field = { width: '100%', padding: '8px 10px', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 13, background: 'var(--card-bg)', color: 'var(--text)', fontFamily: 'inherit' };
export const section = { fontSize: 11, fontWeight: 600, color: 'var(--copper)', textTransform: 'uppercase', letterSpacing: '0.07em', margin: '18px 0 8px' };

export const RefCode = ({ code }) => code ? (
  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', padding: '1px 6px', borderRadius: 5, fontWeight: 500, whiteSpace: 'nowrap' }}>{code}</span>
) : null;

const PILL = {
  ok:      { bg: 'var(--success-bg)', fg: 'var(--success)', bd: 'var(--success-border)' },
  warn:    { bg: 'var(--warn-bg)',    fg: 'var(--warn)',    bd: 'var(--warn-border)' },
  bad:     { bg: 'var(--danger-bg)',  fg: 'var(--danger)',  bd: 'var(--danger-border)' },
  info:    { bg: 'var(--info-bg)',    fg: 'var(--info)',    bd: 'var(--info-border)' },
  copper:  { bg: 'var(--copper-bg)',  fg: 'var(--copper)',  bd: 'var(--copper-border)' },
  neutral: { bg: 'var(--bg2)',        fg: 'var(--text3)',   bd: 'var(--border)' },
};
export const Pill = ({ tone = 'neutral', children, style }) => {
  const t = PILL[tone] || PILL.neutral;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500, background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, whiteSpace: 'nowrap', lineHeight: 1.6, ...style }}>{children}</span>;
};

/** "Verified Aug 2026" / "Needs check" — stale after 12 months, or never. */
export const VerifiedPill = ({ verified_at }) => {
  if (!verified_at) return <Pill tone="warn">Needs check</Pill>;
  const age = (Date.now() - new Date(verified_at).getTime()) / 86400000;
  const when = new Date(verified_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  return age > 365 ? <Pill tone="warn">Checked {when}</Pill> : <Pill tone="ok">✓ {when}</Pill>;
};

export const Phone = ({ value }) => value ? <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, whiteSpace: 'nowrap' }}>{fmtPhone(value)}</span> : <span style={{ color: 'var(--text3)' }}>—</span>;

export const Modal = ({ title, onClose, children, width = 680, footer }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '32px 16px', overflowY: 'auto' }}
    onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
    <div style={{ background: 'var(--card-bg)', borderRadius: 12, width: '100%', maxWidth: width, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '14px 20px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{title}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text3)', lineHeight: 1 }} aria-label="Close">×</button>
      </div>
      <div style={{ padding: '4px 20px 20px' }}>{children}</div>
      {footer && <div style={{ padding: '12px 20px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{footer}</div>}
    </div>
  </div>
);

export const ErrorBox = ({ children }) => children ? (
  <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 7, padding: '10px 14px', margin: '12px 0 4px', fontSize: 12, color: 'var(--danger)' }}>{children}</div>
) : null;

export const Chip = ({ on, onClick, children }) => (
  <button type="button" onClick={onClick}
    style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '1px solid', background: on ? 'var(--navy)' : 'transparent', color: on ? '#fff' : 'var(--text2)', borderColor: on ? 'var(--text)' : 'var(--border)', whiteSpace: 'nowrap' }}>
    {children}
  </button>
);

export const Empty = ({ children }) => <div style={{ textAlign: 'center', padding: 28, color: 'var(--text3)', fontSize: 13 }}>{children}</div>;
